/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { dateClient } from '@utils/date-client';
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { useDraggable } from '@dnd-kit/core';
import { DayCell } from './DayCell';
import './MonthGridStyles.scss';
const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
/** Builds an array of 35 or 42 day Date objects covering the full month grid. */
function buildMonthDays(year, month, timezone) {
    const monthStr = String(month + 1).padStart(2, '0');
    // Anchor to midnight on the 1st of the month in the selected timezone (DST-safe).
    const firstDay = dateClient.tz(`${year}-${monthStr}-01 00:00:00`, timezone);
    const lastDay = firstDay.endOf('month');
    // Start from the Sunday before (or on) the first of the month
    const gridStart = firstDay.subtract(firstDay.day(), 'day');
    // End on the Saturday after (or on) the last of the month
    const endDay = lastDay.day() === 6 ? lastDay : lastDay.add(6 - lastDay.day(), 'day');
    const gridEnd = endDay;
    const days = [];
    let current = gridStart;
    while (current.isBefore(gridEnd) || current.isSame(gridEnd, 'day')) {
        days.push(current.toDate());
        current = current.add(1, 'day');
    }
    return days;
}
/** Groups events by their date key (YYYY-MM-DD). Multi-day events appear on each day they span.
 * Uses the selected timezone for day boundaries so events land on the correct calendar days
 * regardless of the browser's local timezone. End is treated as exclusive (matching FullCalendar). */
function buildEventMap(events, timezone) {
    const map = new Map();
    events.forEach((event) => {
        const start = dateClient(event.start).tz(timezone).startOf('day');
        const endInTz = dateClient(event.end).tz(timezone);
        const endDay = endInTz.startOf('day');
        // If end is exactly at midnight it is already a FullCalendar-style exclusive
        // boundary (holidays are normalised this way by useEvents). Otherwise end has
        // a sub-day time component (e.g. TimeOff events arrive as 23:59:59 local time),
        // meaning the last *visible* day is endDay — promote by 1 so the exclusive loop
        // includes it. IP-13297: fixes TimeOff spanning one day short in MonthGrid.
        const end = endInTz.isSame(endDay) ? endDay : endDay.add(1, 'day');
        // Single-day events whose start and end both fall on the same exclusive day
        // still need at least one iteration.
        const effectiveEnd = start.isSame(end, 'day') ? end.add(1, 'day') : end;
        let current = start;
        while (current.isBefore(effectiveEnd)) {
            const key = current.format('YYYY-MM-DD');
            const existing = map.get(key);
            if (existing) {
                existing.push(event);
            }
            else {
                map.set(key, [event]);
            }
            current = current.add(1, 'day');
        }
    });
    return map;
}
/**
 * Wraps a single event in a dnd-kit draggable.
 *
 * When dragging, this element becomes invisible (opacity 0) to act as a
 * placeholder. The actual drag visual is rendered by DragOverlay in MonthGrid,
 * which portals outside the cell DOM and is never clipped by overflow:hidden.
 */
function DraggableEvent({ event, renderContent }) {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: String(event.id),
        data: { event },
        disabled: event.editable === false
    });
    return (React.createElement("div", Object.assign({ ref: setNodeRef, style: {
            opacity: isDragging ? 0 : 1,
            cursor: event.editable === false ? 'default' : 'grab'
        } }, listeners, attributes), renderContent(event)));
}
/**
 * Custom virtualized month grid.
 *
 * Replaces FullCalendar's dayGridMonth view when calendar optimization is
 * enabled. Only the visible events (up to dayMaxEvents per cell) are mounted;
 * overflow events are deferred to the MoreEventsPopover which mounts lazily.
 *
 * Drag-and-drop is handled by @dnd-kit/core with PointerSensor, providing
 * identical functionality to FullCalendar's month view drag behavior.
 */
export const MonthGrid = React.memo(function MonthGrid({ year, month, timezone = Intl.DateTimeFormat().resolvedOptions().timeZone, events, dayMaxEvents, eventContent, onEventClick, onEventChange }) {
    var _a, _b;
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
    // The event currently being dragged — used to render the DragOverlay.
    const [activeDragEvent, setActiveDragEvent] = useState(null);
    const days = useMemo(() => buildMonthDays(year, month, timezone), [year, month, timezone]);
    const eventMap = useMemo(() => buildEventMap(events, timezone), [events, timezone]);
    const today = dateClient().tz(timezone).startOf('day');
    const monthStr = String(month + 1).padStart(2, '0');
    const currentMonthStart = dateClient.tz(`${year}-${monthStr}-01 00:00:00`, timezone).startOf('month');
    const handleDragStart = useCallback((dragStartEvent) => {
        var _a;
        const event = (_a = dragStartEvent.active.data.current) === null || _a === void 0 ? void 0 : _a.event;
        setActiveDragEvent(event !== null && event !== void 0 ? event : null);
    }, []);
    const handleDragEnd = useCallback((dragEvent) => {
        var _a;
        setActiveDragEvent(null);
        const { active, over } = dragEvent;
        if (!over)
            return;
        const draggedEvent = (_a = active.data.current) === null || _a === void 0 ? void 0 : _a.event;
        if (!draggedEvent)
            return;
        const targetDateStr = over.id;
        const targetDate = dayjs(targetDateStr);
        if (!targetDate.isValid())
            return;
        const originalStart = dayjs(draggedEvent.start);
        const originalEnd = dayjs(draggedEvent.end);
        const diff = originalEnd.diff(originalStart);
        const newStart = targetDate
            .hour(originalStart.hour())
            .minute(originalStart.minute())
            .second(originalStart.second())
            .toDate();
        const newEnd = dayjs(newStart).add(diff, 'millisecond').toDate();
        onEventChange(draggedEvent, newStart, newEnd);
    }, [onEventChange]);
    // Build a stable renderEvent function that wraps DraggableEvent + click handler
    const renderEvent = useCallback((event) => (React.createElement(DraggableEvent, { event: event, renderContent: (e) => {
            var _a, _b, _c, _d;
            return (React.createElement("button", { type: "button", className: "qmb-month-grid__event-btn", onClick: () => onEventClick(e), style: {
                    backgroundColor: (_a = e.backgroundColor) !== null && _a !== void 0 ? _a : undefined,
                    borderColor: (_b = e.borderColor) !== null && _b !== void 0 ? _b : undefined,
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    '--event-color': (_d = (_c = e.backgroundColor) !== null && _c !== void 0 ? _c : e.borderColor) !== null && _d !== void 0 ? _d : undefined
                } }, eventContent(e)));
        } })), [eventContent, onEventClick]);
    return (React.createElement(DndContext, { sensors: sensors, onDragStart: handleDragStart, onDragEnd: handleDragEnd },
        React.createElement("div", { className: "qmb-month-grid", "aria-label": `${dayjs(new Date(year, month)).format('MMMM YYYY')} calendar` },
            React.createElement("div", { className: "qmb-month-grid__header", "aria-hidden": "true" }, WEEK_DAYS.map((day) => (React.createElement("div", { key: day, className: "qmb-month-grid__header-cell" }, day)))),
            React.createElement("div", { className: "qmb-month-grid__body" }, days.map((date) => {
                var _a;
                const dateKey = dateClient(date).tz(timezone).format('YYYY-MM-DD');
                const dayEvents = (_a = eventMap.get(dateKey)) !== null && _a !== void 0 ? _a : [];
                const isCurrentMonth = dateClient(date).tz(timezone).isSame(currentMonthStart, 'month');
                const isToday = dateClient(date).tz(timezone).isSame(today, 'day');
                return (React.createElement(DayCell, { key: dateKey, date: date, timezone: timezone, isCurrentMonth: isCurrentMonth, isToday: isToday, events: dayEvents, dayMaxEvents: dayMaxEvents, renderEvent: renderEvent }));
            }))),
        React.createElement(DragOverlay, { dropAnimation: null }, activeDragEvent && (React.createElement("div", { className: "qmb-month-grid__drag-overlay" },
            React.createElement("button", { type: "button", className: "qmb-month-grid__event-btn", style: {
                    backgroundColor: (_a = activeDragEvent.backgroundColor) !== null && _a !== void 0 ? _a : undefined,
                    borderColor: (_b = activeDragEvent.borderColor) !== null && _b !== void 0 ? _b : undefined
                } }, eventContent(activeDragEvent)))))));
});
